import { Utils } from 'core/utils';
import { ControllerOptions, normalizedOptions, TextApplianceResult,
         TextController, TextControllerBase, TextEditingResult
       } from './text.controller';
import { TextControllerMethods, TextEditingRefusalReason
       } from './text.controller.methods';
import { FontDescription, renderedContent, Text, TextChunk, TextEffects,
         updateFont
       } from './text.model';

function checkChangedModel(model: Text, changedModel: Text): TextEditingResult
{
  const unicodeCheckResult =
      TextControllerMethods.checkForInvalidSymbols(changedModel);
  if (unicodeCheckResult !== TextEditingRefusalReason.None) {
    return <TextEditingResult> {
      type: TextApplianceResult.Failure,
      reason: TextEditingRefusalReason.CharacterNotSupported,
      model: model,
      position: unicodeCheckResult
    };
  }

  const reason =
      TextControllerMethods.getUsageAvailableAreaComparison(changedModel);

  if (reason !== TextEditingRefusalReason.None) {
    return <TextEditingResult> {
      type: TextApplianceResult.Failure,
      reason: reason,
      model: model,
    };
  }
  else {
    return <TextEditingResult> {
      type: TextApplianceResult.Success,
      model: changedModel,
    };
  }
}

export const ZoomModeLimit: TextController = {
  setColor: TextControllerBase.setColor,
  setBgColor: TextControllerBase.setBgColor,
  setHorizontalAlignment: TextControllerBase.setHorizontalAlignment,
  setVerticalAlignment: TextControllerBase.setVerticalAlignment,
  setAvailableArea: TextControllerBase.setAvailableArea,

  setText: (
    model: Text,
    newText: Array<TextChunk>,
    _?: ControllerOptions
  ): TextEditingResult =>
  {
    if (TextControllerMethods.exceedsMaxTextLength(newText)) {
      return <TextEditingResult> {
        type: TextApplianceResult.Failure,
        reason: TextEditingRefusalReason.TextTooLong,
        model: model,
      };
    }

    let workingModel = Utils.deepCopy(model);
    newText = TextControllerMethods.removeVariationSelectors(newText);

    for (let chunkIdx = 0; chunkIdx < newText.length; chunkIdx++) {
      const chunk = newText[chunkIdx];
      if (!TextControllerMethods.textIsCoveredByUnicodeRanges(
          renderedContent(chunk.content, model.effects),
          model.fontDescription.unicodeRanges)) {
        return <TextEditingResult> {
          type: TextApplianceResult.Failure,
          reason: TextEditingRefusalReason.CharacterNotSupported,
          model: model,
          position: {
            chunk: chunkIdx,
            character: -1
          }
        };
      }
    }

    workingModel.text = newText;
    const reason =
        TextControllerMethods.getUsageAvailableAreaComparison(workingModel);

    if (reason !== TextEditingRefusalReason.None) {
      return <TextEditingResult> {
        type: TextApplianceResult.Failure,
        reason: reason,
        model: model,
      };
    }
    else {
      return <TextEditingResult>  {
        type: TextApplianceResult.Success,
        model: workingModel
      };
    }
  },

  setFont: (
    model: Text,
    fontDescription: FontDescription,
    _?: ControllerOptions
  ): TextEditingResult =>
  {
    const changedModel = updateFont(Utils.deepCopy(model), fontDescription);

    return checkChangedModel(model, changedModel);
  },

  setEffects: (
    model: Text,
    effects: TextEffects,
    _?: ControllerOptions
  ): TextEditingResult =>
  {
    const changedModel = Utils.deepCopy(model);
    changedModel.effects = effects;

    return checkChangedModel(model, changedModel);
  },

  setFontSize: (
    model: Text,
    fontSize: number,
    rawOptions?: ControllerOptions
  ): TextEditingResult =>
  {
    const options = normalizedOptions(rawOptions);
    let workingModel = Utils.deepCopy(model);
    workingModel.fontSize = fontSize;

    if (fontSize > options.maxFontSize) {
      return <TextEditingResult> {
        type: TextApplianceResult.Failure,
        reason: TextEditingRefusalReason.TextTooWide,
        model: model,
      };
    }

    if (fontSize < options.minFontSize) {
      return <TextEditingResult> {
        type: TextApplianceResult.Failure,
        reason: TextEditingRefusalReason.MinFontSizeUndershot,
        model: model,
      };
    }

    const reason =
        TextControllerMethods.getUsageAvailableAreaComparison(workingModel);
    if (reason !== TextEditingRefusalReason.None) {
      return <TextEditingResult> {
        type: TextApplianceResult.Failure,
        reason: reason,
        model: model,
      };
    }

    return <TextEditingResult> {
      type: TextApplianceResult.Success,
      model: workingModel,
    };
  }
};
